import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Divider,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Subtitle2,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { MenuButton } from '@shared/components';

import { MOCK_USER_NAMES } from '../../data';
import {
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS,
  TASK_STATUS_OPTIONS,
  TChangeV2,
  TPlanTask,
  TTaskPriority,
  TTaskStatus,
} from '../../models';
import { formatDate, formatDateTime, isTaskComplete } from '../../utils';
import { useChangeV2Store } from '../../state/ChangeV2Store';
import { DefinitionList, TDefinitionItem } from '../shared';
import { TaskDocumentLinks } from '../shared/TaskDocumentLinks';
import { TaskStatusBadge, TaskStatusIcon } from './TaskStatusBadge';
import { TaskSolutionProposal } from './TaskSolutionProposal';

const useStyles = makeStyles({
  surface: { width: 'min(800px, calc(100vw - 32px))', maxWidth: '800px' },
  content: { maxHeight: 'min(720px, calc(100vh - 180px))', overflowY: 'auto' },
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  taskHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  titleBlock: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0 },
  headRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  statusButton: { minWidth: '172px', flexShrink: 0 },
  muted: { color: tokens.colorNeutralForeground3 },
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: tokens.spacingVerticalM,
    '@media (max-width: 640px)': { gridTemplateColumns: '1fr' },
  },
  dropdown: { minWidth: 0, width: '100%' },
  wide: { gridColumn: '1 / -1' },
  essentials: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: tokens.spacingVerticalM,
    '@media (max-width: 640px)': { gridTemplateColumns: '1fr' },
  },
  essential: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  dateInput: {
    boxSizing: 'border-box',
    minWidth: 0,
    width: '100%',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    fontFamily: tokens.fontFamilyBase,
  },
});

const isoToDateInput = (iso?: string): string => (iso ? iso.slice(0, 10) : '');
const dateInputToIso = (value: string): string | undefined => (value ? `${value}T00:00:00.000Z` : undefined);

type TProps = {
  change: TChangeV2;
  task?: TPlanTask;
  mode: 'plan' | 'execution';
  readOnly?: boolean;
  onCompleteTask?: (task: TPlanTask) => void;
  onClose: () => void;
};

// The task detail dialog (prompt Parts 7 and 8). One dialog serves planning and
// execution. Core task fields use a compact grid and execution exposes status as
// the primary workflow action.
export const TaskDetailsDialog = ({ change, task, mode, readOnly = false, onCompleteTask, onClose }: TProps) => {
  const classes = useStyles();
  const { updatePlanTask, removePlanTask, startTask } = useChangeV2Store();

  const open = Boolean(task);
  const workPackage = change.plan?.workPackages.find((wp) => wp.id === task?.workPackageId);
  const editable = mode === 'plan' && !readOnly;

  const essentials: TDefinitionItem[] = task
    ? [
        { label: 'Work package', value: workPackage?.name ?? '—' },
        { label: 'Task type', value: task.taskType },
        { label: 'Owner', value: task.owner || 'Unassigned' },
        { label: 'Responsible function', value: task.responsibleFunction || '—' },
        { label: 'Due date', value: task.dueDate ? formatDate(task.dueDate) : 'Not set' },
        { label: 'Priority', value: task.priority },
      ]
    : [];

  const handlePatch = (patch: Partial<TPlanTask>) => {
    if (task) updatePlanTask(change.id, task.id, patch);
  };

  const handleStatusChange = (status: TTaskStatus) => {
    if (!task || task.status === status) return;

    if (status === TASK_STATUS.COMPLETE) {
      onClose();
      onCompleteTask?.(task);
      return;
    }

    if (task.status === TASK_STATUS.NOT_STARTED && status === TASK_STATUS.IN_PROGRESS) {
      startTask(change.id, task.id);
      return;
    }

    handlePatch({ status, completedBy: undefined, completedAt: undefined, deviations: undefined });
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={classes.surface}>
        <DialogBody>
          <DialogTitle
            action={<Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />}
          >
            Task details
          </DialogTitle>
          <DialogContent className={classes.content}>
            {task ? (
              <div key={task.id} className={classes.body}>
                <div className={classes.taskHeader}>
                  <div className={classes.titleBlock}>
                    <div className={classes.headRow}>
                      <Subtitle2>{task.title}</Subtitle2>
                    </div>
                    <div className={classes.headRow}>
                      {task.external ? <Caption1 className={classes.muted}>External gate</Caption1> : null}
                      {task.origin !== 'Manual' ? (
                        <Caption1 className={classes.muted}>From {task.origin.toLowerCase()}</Caption1>
                      ) : null}
                    </div>
                  </div>
                  {mode === 'execution' ? (
                    <Menu positioning="below-end">
                      <MenuTrigger disableButtonEnhancement>
                        <MenuButton
                          className={classes.statusButton}
                          size="large"
                          appearance={task.status === TASK_STATUS.NOT_STARTED ? 'secondary' : 'primary'}
                          variant={task.status === TASK_STATUS.IN_PROGRESS ? 'blue' : 'default'}
                          icon={<TaskStatusIcon status={task.status} />}
                        >
                          {task.status}
                        </MenuButton>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {TASK_STATUS_OPTIONS.filter((status) => status !== task.status).map((status) => (
                            <MenuItem
                              key={status}
                              icon={<TaskStatusIcon status={status} />}
                              disabled={status === TASK_STATUS.COMPLETE && !onCompleteTask}
                              onClick={() => handleStatusChange(status)}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  ) : (
                    <TaskStatusBadge status={task.status} />
                  )}
                </div>

                {/* Essentials - editable in planning, read-only in execution. */}
                {editable ? (
                  <div className={classes.fields}>
                    <Field label="Owner">
                      <Dropdown
                        className={classes.dropdown}
                        selectedOptions={task.owner ? [task.owner] : ['']}
                        value={task.owner || 'Unassigned'}
                        onOptionSelect={(_, data) => handlePatch({ owner: data.optionValue || undefined })}
                      >
                        <Option value="" text="Unassigned">
                          Unassigned
                        </Option>
                        {MOCK_USER_NAMES.map((name) => (
                          <Option key={name} value={name} text={name}>
                            {name}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field label="Due date">
                      <input
                        type="date"
                        className={classes.dateInput}
                        defaultValue={isoToDateInput(task.dueDate)}
                        onChange={(event) => handlePatch({ dueDate: dateInputToIso(event.target.value) })}
                      />
                    </Field>
                    <Field label="Priority">
                      <Dropdown
                        className={classes.dropdown}
                        selectedOptions={[task.priority]}
                        value={task.priority}
                        onOptionSelect={(_, data) => handlePatch({ priority: data.optionValue as TTaskPriority })}
                      >
                        {TASK_PRIORITY_OPTIONS.map((priority) => (
                          <Option key={priority} value={priority} text={priority}>
                            {priority}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field label="Description" className={classes.wide}>
                      <Textarea
                        defaultValue={task.description ?? ''}
                        resize="vertical"
                        onBlur={(event) => handlePatch({ description: event.target.value || undefined })}
                      />
                    </Field>
                  </div>
                ) : (
                  <>
                    <div className={classes.essentials}>
                      {essentials.map((item) => (
                        <div key={item.label} className={classes.essential}>
                          <Caption1 className={classes.muted}>{item.label}</Caption1>
                          <Body1>{item.value}</Body1>
                        </div>
                      ))}
                    </div>
                    {task.description ? (
                      <div className={classes.block}>
                        <Body1Strong>Description</Body1Strong>
                        <Body1>{task.description}</Body1>
                      </div>
                    ) : null}
                  </>
                )}

                {task.documents?.length ? (
                  <div className={classes.block}>
                    <Body1Strong>Documents</Body1Strong>
                    <TaskDocumentLinks documents={task.documents} />
                  </div>
                ) : null}

                <Divider />

                <TaskSolutionProposal key={task.id} change={change} task={task} readOnly={readOnly} />

                <Accordion multiple collapsible defaultOpenItems={editable ? ['completion'] : []}>
                  <AccordionItem value="completion">
                    <AccordionHeader>Completion</AccordionHeader>
                    <AccordionPanel>
                      <div className={classes.fields}>
                        {editable ? (
                          <Field label="Completion criteria" className={classes.wide}>
                            <Textarea
                              defaultValue={task.completionCriteria ?? ''}
                              resize="vertical"
                              onBlur={(event) => handlePatch({ completionCriteria: event.target.value || undefined })}
                            />
                          </Field>
                        ) : (
                          <DefinitionList
                            items={[{ label: 'Completion criteria', value: task.completionCriteria || '—' }]}
                          />
                        )}
                      </div>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>

                {mode === 'execution' && isTaskComplete(task) ? (
                  <>
                    <Divider />
                    <DefinitionList
                      items={[
                        { label: 'Completed by', value: task.completedBy ?? '—' },
                        {
                          label: 'Completion date',
                          value: task.completedAt ? formatDateTime(task.completedAt) : '—',
                        },
                        ...(task.deviations ? [{ label: 'Deviations', value: task.deviations }] : []),
                      ]}
                    />
                  </>
                ) : null}

                {mode === 'plan' && !readOnly && task.origin === 'Manual' ? (
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => {
                      removePlanTask(change.id, task.id);
                      onClose();
                    }}
                  >
                    Remove task
                  </Button>
                ) : null}
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
